import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash, Wallet } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { money, fdate } from "@/lib/format";
import { PageHeader, EmptyState, StatusBadge } from "@/components/common";
import { TextField, SelectField, AreaField } from "@/components/fields";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const EMPTY = { nombre: "", puesto: "", salario_semanal: "", fecha_ingreso: "", telefono: "", banco: "", cuenta: "", clabe: "", notas: "" };
const TIPOS_INC = [["bono", "Bono"], ["descuento", "Descuento"], ["adelanto", "Adelanto"], ["falta", "Falta"], ["vacaciones", "Vacaciones"], ["permiso", "Permiso"], ["incapacidad", "Incapacidad"], ["otro", "Otro"]].map(([value, label]) => ({ value, label }));

const iso = (d) => d.toISOString().slice(0, 10);
function weekOf(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  const day = (d.getDay() + 6) % 7;
  const mon = new Date(d); mon.setDate(d.getDate() - day);
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  const jan1 = new Date(mon.getFullYear(), 0, 1);
  const week = Math.ceil(((mon - jan1) / 86400000 + jan1.getDay() + 1) / 7);
  return { inicio: iso(mon), fin: iso(sun), label: `Semana ${week}, ${mon.getFullYear()}` };
}

export default function RRHH() {
  const [emps, setEmps] = useState([]);
  const [payrolls, setPayrolls] = useState([]);
  const [incs, setIncs] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false); const [form, setForm] = useState(EMPTY); const [editId, setEditId] = useState(null);
  const [day, setDay] = useState(iso(new Date()));
  const [corte, setCorte] = useState(null);
  const [account, setAccount] = useState("");
  const [inc, setInc] = useState({ employee_id: "", tipo: "bono", fecha: iso(new Date()), monto: "", descripcion: "" });
  const navigate = useNavigate();
  const wk = weekOf(day);

  const loadBase = () => {
    api.get("/rh/employees").then((r) => setEmps(r.data));
    api.get("/rh/payroll").then((r) => setPayrolls(r.data));
    api.get("/rh/incidencias").then((r) => setIncs(r.data));
    api.get("/accounts").then((r) => setAccounts(r.data));
  };
  useEffect(() => { loadBase(); }, []);

  const loadCorte = useCallback(() => { api.get("/rh/payroll/corte", { params: { inicio: wk.inicio, fin: wk.fin } }).then((r) => setCorte(r.data)); }, [wk.inicio, wk.fin]);
  useEffect(() => { loadCorte(); }, [loadCorte]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const saveEmp = async () => {
    if (!form.nombre) return toast.error("Nombre obligatorio");
    try { const p = { ...form, salario_semanal: parseFloat(form.salario_semanal) || 0, fecha_ingreso: form.fecha_ingreso || null }; if (editId) await api.put(`/rh/employees/${editId}`, p); else await api.post("/rh/employees", p); toast.success("Empleado guardado"); setOpen(false); loadBase(); } catch (e) { toast.error(errMsg(e)); }
  };
  const empName = (id) => emps.find((e) => e.id === id)?.nombre || "—";
  const addInc = async () => { if (!inc.employee_id) return toast.error("Selecciona empleado"); try { await api.post("/rh/incidencias", { ...inc, monto: parseFloat(inc.monto) || 0 }); toast.success("Incidencia registrada"); setInc({ ...inc, monto: "", descripcion: "" }); loadBase(); } catch (e) { toast.error(errMsg(e)); } };
  const pagarNomina = async () => {
    if (!corte || corte.rows.length === 0) return toast.error("No hay empleados activos");
    try { const { data } = await api.post("/rh/payroll/corte", { inicio: wk.inicio, fin: wk.fin, account_id: account || null }); toast.success(`Nómina pagada: ${data.pagados} empleado(s) · ${money(data.total)}`); loadBase(); loadCorte(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="rrhh-page">
      <PageHeader title="Nómina y Recursos Humanos" subtitle="Empleados, incidencias y corte de nómina semanal" />
      <Tabs defaultValue="empleados">
        <TabsList className="rounded-sm">
          <TabsTrigger value="empleados" data-testid="tab-empleados">Empleados</TabsTrigger>
          <TabsTrigger value="incidencias" data-testid="tab-incidencias">Incidencias</TabsTrigger>
          <TabsTrigger value="nomina" data-testid="tab-nomina">Nómina</TabsTrigger>
          <TabsTrigger value="historial" data-testid="tab-historial">Historial</TabsTrigger>
        </TabsList>

        <TabsContent value="empleados" className="mt-4">
          <div className="flex justify-end mb-3"><Button data-testid="new-employee-btn" className="rounded-sm gap-2" onClick={() => { setForm(EMPTY); setEditId(null); setOpen(true); }}><Plus size={16} /> Nuevo empleado</Button></div>
          {emps.length === 0 ? <EmptyState title="Sin empleados" hint="Registra al personal de la inmobiliaria." /> : (
            <div className="bg-card border border-border rounded-sm overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Nombre</TableHead><TableHead>Puesto</TableHead><TableHead className="text-right">Salario semanal</TableHead><TableHead>Ingreso</TableHead><TableHead className="text-right">Acciones</TableHead></TableRow></TableHeader>
                <TableBody>
                  {emps.map((e) => (
                    <TableRow key={e.id} data-testid={`employee-row-${e.id}`} className="cursor-pointer" onClick={() => navigate(`/rrhh/${e.id}`)}>
                      <TableCell className="font-medium">{e.nombre}</TableCell><TableCell>{e.puesto || "—"}</TableCell><TableCell className="text-right tabular-nums">{money(e.salario_semanal)}</TableCell><TableCell className="text-xs">{e.fecha_ingreso ? fdate(e.fecha_ingreso) : "—"}</TableCell>
                      <TableCell className="text-right" onClick={(ev) => ev.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="rounded-sm" onClick={() => { setForm({ ...EMPTY, ...e }); setEditId(e.id); setOpen(true); }}>✎</Button>
                        <Button variant="ghost" size="icon" className="rounded-sm text-red-600" onClick={async () => { if (window.confirm("¿Eliminar?")) { await api.delete(`/rh/employees/${e.id}`); loadBase(); } }}><Trash size={16} /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="incidencias" className="mt-4">
          <div className="bg-card border border-border rounded-sm p-4 grid grid-cols-1 sm:grid-cols-5 gap-3 items-end">
            <SelectField label="Empleado" value={inc.employee_id} onChange={(v) => setInc((i) => ({ ...i, employee_id: v }))} testId="inc-emp" options={emps.map((e) => ({ value: e.id, label: e.nombre }))} />
            <SelectField label="Tipo" value={inc.tipo} onChange={(v) => setInc((i) => ({ ...i, tipo: v }))} testId="inc-tipo" options={TIPOS_INC} />
            <TextField label="Fecha" type="date" value={inc.fecha} onChange={(v) => setInc((i) => ({ ...i, fecha: v }))} />
            <TextField label="Monto" type="number" value={inc.monto} onChange={(v) => setInc((i) => ({ ...i, monto: v }))} testId="inc-monto" />
            <Button className="rounded-sm gap-1" data-testid="add-inc-btn" onClick={addInc}><Plus size={16} /> Agregar</Button>
          </div>
          {incs.length === 0 ? <EmptyState title="Sin incidencias" hint="Registra bonos, descuentos, adelantos, faltas, etc." /> : (
            <div className="bg-card border border-border rounded-sm overflow-hidden mt-4">
              <Table><TableHeader><TableRow><TableHead>Fecha</TableHead><TableHead>Empleado</TableHead><TableHead>Tipo</TableHead><TableHead className="text-right">Monto</TableHead><TableHead>Descripción</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>{incs.map((i) => (<TableRow key={i.id}><TableCell className="text-xs">{fdate(i.fecha)}</TableCell><TableCell>{empName(i.employee_id)}</TableCell><TableCell className="capitalize">{i.tipo}</TableCell><TableCell className="text-right tabular-nums">{money(i.monto)}</TableCell><TableCell>{i.descripcion || "—"}</TableCell><TableCell className="text-right"><Button variant="ghost" size="icon" className="rounded-sm text-red-600" onClick={async () => { await api.delete(`/rh/incidencias/${i.id}`); loadBase(); }}><Trash size={16} /></Button></TableCell></TableRow>))}</TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="nomina" className="mt-4">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
            <div><h3 className="font-display font-bold">Corte de nómina</h3><p className="text-sm text-muted-foreground">Selecciona la semana para ver y pagar la nómina.</p></div>
            <div className="flex items-end gap-2">
              <div className="text-right text-sm font-medium tabular-nums px-3 py-2 border border-border rounded-sm bg-card" data-testid="week-label">{wk.label}</div>
              <input type="date" data-testid="week-day" value={day} onChange={(e) => setDay(e.target.value)} className="rounded-sm border border-input h-9 px-2 bg-background" />
            </div>
          </div>
          <div className="bg-card border border-border rounded-sm overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Empleado</TableHead><TableHead>Periodo</TableHead><TableHead className="text-right">Sueldo base</TableHead><TableHead className="text-right">Ajustes</TableHead><TableHead className="text-right">Neto a pagar</TableHead></TableRow></TableHeader>
              <TableBody>
                {!corte || corte.rows.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No hay empleados activos</TableCell></TableRow>
                ) : corte.rows.map((r) => (
                  <TableRow key={r.employee_id} data-testid={`corte-row-${r.employee_id}`}>
                    <TableCell className="font-medium">{r.empleado}</TableCell>
                    <TableCell className="text-xs">{wk.inicio} a {wk.fin}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(r.base)}</TableCell>
                    <TableCell className={`text-right tabular-nums ${r.ajustes < 0 ? "text-red-600" : "text-emerald-600"}`}>{money(r.ajustes)}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{money(r.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="flex items-center justify-between px-4 py-3 border-t border-border">
              <span className="text-sm">Total nómina · {wk.inicio} a {wk.fin}</span>
              <span className="font-display font-bold tabular-nums text-lg" data-testid="corte-total">{money(corte?.total)}</span>
            </div>
          </div>
          <div className="flex items-end justify-end gap-2 mt-4">
            <div className="w-64"><SelectField label="Cuenta de pago" value={account} onChange={setAccount} testId="corte-account" options={accounts.map((a) => ({ value: a.id, label: `${a.nombre} (${a.proposito})` }))} /></div>
            <Button className="rounded-sm gap-2" data-testid="pay-corte-btn" onClick={pagarNomina}><Wallet size={16} /> Pagar nómina</Button>
          </div>
        </TabsContent>

        <TabsContent value="historial" className="mt-4">
          {payrolls.length === 0 ? <EmptyState title="Sin nóminas" hint="Aún no se ha pagado ninguna nómina." /> : (
            <div className="bg-card border border-border rounded-sm overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Empleado</TableHead><TableHead>Semana</TableHead><TableHead className="text-right">Base</TableHead><TableHead className="text-right">Ajustes</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Estado</TableHead></TableRow></TableHeader>
                <TableBody>
                  {payrolls.map((p) => (
                    <TableRow key={p.id} data-testid={`payroll-row-${p.id}`}>
                      <TableCell className="font-medium">{p.empleado}</TableCell><TableCell className="text-xs">{fdate(p.inicio)} → {fdate(p.fin)}</TableCell><TableCell className="text-right tabular-nums">{money(p.base)}</TableCell><TableCell className="text-right tabular-nums text-red-600">{money((p.bonos || 0) - (p.descuentos || 0) - (p.adelantos || 0) - (p.falta_desc || 0))}</TableCell><TableCell className="text-right tabular-nums font-semibold">{money(p.total)}</TableCell><TableCell><StatusBadge estado={p.estado === "pagada" ? "pagado" : "pendiente"} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Editar empleado" : "Nuevo empleado"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <TextField label="Nombre" value={form.nombre} onChange={set("nombre")} testId="employee-nombre" col={2} />
            <TextField label="Puesto" value={form.puesto} onChange={set("puesto")} />
            <TextField label="Salario semanal" type="number" value={form.salario_semanal} onChange={set("salario_semanal")} testId="employee-salario" />
            <TextField label="Fecha de ingreso" type="date" value={form.fecha_ingreso} onChange={set("fecha_ingreso")} />
            <TextField label="Teléfono" value={form.telefono} onChange={set("telefono")} />
            <TextField label="Banco" value={form.banco} onChange={set("banco")} />
            <TextField label="Cuenta" value={form.cuenta} onChange={set("cuenta")} />
            <TextField label="CLABE" value={form.clabe} onChange={set("clabe")} col={2} />
            <AreaField label="Notas" value={form.notas} onChange={set("notas")} />
          </div>
          <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancelar</Button><Button className="rounded-sm" data-testid="save-employee-btn" onClick={saveEmp}>Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
